// src/components/Dashboard/PerformanceMetricWidget.tsx
import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import {
  getUserPerformanceMetric,
  getAirportPerformanceMetric,
  getSystemPerformanceMetric,
  DashboardApiError,
} from '../../services/dashboardApi';
import { isIntegerUserId } from '../../services/authApi';

interface PerformanceMetricWidgetProps {
  type: 'user' | 'airport' | 'system';
  userId?: number | string; // May be GUID (string) or integer (number)
  airportId?: number;
  title?: string;
}

export default function PerformanceMetricWidget({
  type,
  userId,
  airportId,
  title,
}: PerformanceMetricWidgetProps) {
  const [kpiScore, setKpiScore] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false); // Default minimized

  useEffect(() => {
    const loadMetric = async () => {
      try {
        setLoading(true);
        setError(null);

        // For airport type, wait for airportId to be available
        if (type === 'airport' && !airportId) {
          // Don't show error immediately - airportId might still be loading
          // Keep loading state until airportId is available
          setLoading(true);
          return;
        }

        let response;
        if (type === 'user' && userId && isIntegerUserId(userId)) {
          response = await getUserPerformanceMetric(userId);
        } else if (type === 'user' && userId && typeof userId === 'string') {
          // userId is a GUID - backend should return integer userId, but fell back to GUID
          // This can happen if user doesn't exist in Users table (fallback behavior)
          setError('User performance metrics unavailable: userId format not supported (GUID fallback)');
          setLoading(false);
          return;
        } else if (type === 'airport' && airportId) {
          response = await getAirportPerformanceMetric(airportId);
        } else if (type === 'system') {
          response = await getSystemPerformanceMetric();
        } else {
          // Only show error if we have all required info but still can't proceed
          if (type === 'airport') {
            // Airport type but no airportId - might still be loading
            setLoading(true);
            return;
          }
          setError('Missing required parameters');
          setLoading(false);
          return;
        }

        setKpiScore(response.kpiScore);
      } catch (err) {
        const apiError = err as DashboardApiError;
        setError(apiError.message || 'Failed to load performance metric');
        console.error('Error loading performance metric:', err);
      } finally {
        setLoading(false);
      }
    };

    loadMetric();
  }, [type, userId, airportId]);

  const formatKpiScore = (score: number): string => {
    if (score > 0) {
      return `+${score.toFixed(2)} days`;
    } else if (score < 0) {
      return `${score.toFixed(2)} days`;
    } else {
      return '0.00 days';
    }
  };

  const getKpiColor = (score: number): 'error' | 'success' | 'default' => {
    // Positive KPI = behind schedule (red)
    // Negative KPI = ahead of schedule (green)
    // Zero = on time (gray)
    if (score > 0) return 'error';
    if (score < 0) return 'success';
    return 'default';
  };

  const getSubtitle = (): string => {
    switch (type) {
      case 'user':
        return 'Personal Performance';
      case 'airport':
        return 'Airport Performance';
      case 'system':
        return 'Composite KPI';
      default:
        return 'Performance';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
            <CircularProgress size={40} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (kpiScore === null) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            No performance data available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent sx={{ p: expanded ? 2 : 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: expanded ? 1 : 0 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {title || getSubtitle()}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{ ml: 'auto' }}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
        
        {/* Minimized View - Always visible */}
        <Box sx={{ textAlign: 'center', py: expanded ? 0 : 1 }}>
          <Typography
            variant={expanded ? 'h3' : 'h4'}
            component="div"
            sx={{
              fontWeight: 'bold',
              color: (theme) => {
                const color = getKpiColor(kpiScore);
                if (color === 'error') return theme.palette.error.main;
                if (color === 'success') return theme.palette.success.main;
                return theme.palette.text.primary;
              },
            }}
          >
            {formatKpiScore(kpiScore)}
          </Typography>
          {expanded && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {kpiScore > 0
                ? 'Behind schedule'
                : kpiScore < 0
                ? 'Ahead of schedule'
                : 'On time'}
            </Typography>
          )}
        </Box>

        {/* Expanded Details */}
        <Collapse in={expanded}>
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              KPI Score Breakdown:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {kpiScore > 0
                ? `Tasks are ${kpiScore.toFixed(2)} days behind schedule on average.`
                : kpiScore < 0
                ? `Tasks are ${Math.abs(kpiScore).toFixed(2)} days ahead of schedule on average.`
                : 'All tasks are on schedule.'}
            </Typography>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

